<script setup lang="ts">
import { Scope, ScopeKey } from '@/views/enumerations/types';
import { useFormMode } from '@/components/forms/useFormMode';
import { localizedEnumCompare, withLastItemCompare } from '@/utils/compareAndSort';
import { truthy } from '@/utils/helper';
import { getTranslationPrefix, ScopeType } from '@/utils/translationPrefix';
import { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import { uniq } from 'lodash';
import { NIL } from 'uuid';
import { useFieldArray } from 'vee-validate';
import { computed, toRef } from 'vue';

const { t } = useTranslation();

const mode = useFormMode();

const props = withDefaults(
  defineProps<{
    name: string;
    scopeOptions: Scope[];
    scopeKey: ScopeKey;
    disabled?: boolean;
    disableSettedOptions?: boolean;
    scopeType?: ScopeType;
    lastItemKey?: string;
  }>(),
  {
    scopeType: 'qaEquipmentScope',
    lastItemKey: 'OtherDosimetryQaEquipment',
    disableSettedOptions: false,
  },
);

const scopes = useFieldArray<Scope>(toRef(() => props.name));
const selectedScopes = [...scopes.fields.value];

const availableScopes = computed(() => listScopes(props.scopeOptions, props.scopeKey));

function rtSystemCategoryForSubCategory(subCategory?: string) {
  const foundScopeOption = props.scopeOptions.find(
    (scopeOption) => scopeOption.rtSystemSubCategory === subCategory,
  );
  return foundScopeOption?.rtSystemCategory;
}

// Creates a list of scopes where everything is set to null except the scope-property <key> and sorts this list localized alphabetically with a defined entry at the end
function listScopes(scopesToList: Scope[], key: ScopeKey = 'rtSystemCategory'): Scope[] {
  const targetOptions = uniq(scopesToList.map((scope) => scope[key]));

  const enumPrefix = getTranslationPrefix(props.scopeType, key);

  function isLastItem(scope: Scope): boolean {
    return scope[props.scopeKey] === props.lastItemKey;
  }

  return targetOptions
    .map((option) => {
      return {
        id: NIL,
        rtSystemCategory: rtSystemCategoryForSubCategory(option || undefined),
        rtSystemSubCategory: null,
        ...{
          [key]: option,
        },
      };
    })
    .toSorted(
      withLastItemCompare((x: Scope, y: Scope) => {
        const a = x[props.scopeKey] || undefined;
        const b = y[props.scopeKey] || undefined;

        if (enumPrefix && a && b) {
          return localizedEnumCompare(enumPrefix)(a, b);
        } else {
          return 0;
        }
      }, isLastItem),
    );
}

function toggle(scope: Scope) {
  const scopeIndex = scopes.fields.value.findIndex(
    (scopeField) => scopeField.value[props.scopeKey] === scope[props.scopeKey],
  );

  if (scopeIndex < 0) {
    const option = availableScopes.value.find(
      (opt) => opt[props.scopeKey] === scope[props.scopeKey],
    );
    if (option) {
      scopes.push(option);
    }
  } else {
    scopes.remove(scopeIndex);
  }
}

function isEntryDisabled(scope: Scope) {
  if (props.disabled || mode.value !== 'edit') return props.disabled;

  const isInitialSelected = availableScopes.value.some((option) =>
    selectedScopes.some(
      (selectedScope) =>
        option[props.scopeKey] === selectedScope.value[props.scopeKey] && scope === option,
    ),
  );
  // .some((initialSelectedScope) => initialSelectedScope === scope);

  return Boolean(isSelected(scope) && isInitialSelected && props.disableSettedOptions);
}

const selectedEntries = computed(() =>
  scopes.fields.value
    .map((scope) =>
      availableScopes.value.find(
        (option) => option[props.scopeKey] === scope.value[props.scopeKey],
      ),
    )
    .filter(truthy),
);

const isSelected = (scope: Scope) =>
  scopes.fields.value.some(
    (selectedScope) => selectedScope.value[props.scopeKey] === scope[props.scopeKey],
  );

function getTranslationForKey(key: string | undefined | null) {
  return t(`${getTranslationPrefix(props.scopeType, props.scopeKey)}.${key}` as I18nKey);
}
</script>

<template>
  <div v-if="mode === 'details'" class="mt-2">
    <v-chip v-for="(scope, index) in selectedEntries" :key="index" class="text-body-2 mr-1 mb-2">
      <span>
        {{ getTranslationForKey(scope[scopeKey]) }}
      </span>
    </v-chip>
  </div>

  <v-list v-else selectStrategy="classic" class="bg-transparent">
    <!-- add list item for each iteration -->
    <v-list-item
      v-for="(scope, index) in availableScopes"
      :key="index"
      :disabled="isEntryDisabled(scope)"
      class="pl-2"
      :value="scope"
      rounded
      @click="toggle(scope)"
    >
      <template #prepend>
        <v-list-item-action start class="mr-0">
          <v-checkbox-btn :modelValue="isSelected(scope)" :disabled="isEntryDisabled(scope)" />
        </v-list-item-action>
      </template>

      <v-list-item-title class="text-body-1 font-weight-medium text-primary-darken-2"
        >{{ getTranslationForKey(scope[scopeKey]) }}
      </v-list-item-title>
    </v-list-item>
  </v-list>

  <p
    v-if="selectedEntries.length === 0 && mode === 'details'"
    class="text-body-1 font-weight-semibold text-primary-lighten-2 mb-1"
  >
    {{ $t('NoCategoriesSelectedMsg') }}
  </p>
</template>

<style scoped lang="scss">
:deep(.v-list-item) {
  min-height: 36px;

  .v-list-item__overlay {
    background: unset;
  }

  &:hover {
    background: rgb(var(--v-theme-secondary-lighten-5));
  }
  .icon {
    margin-right: 8px;
  }
}
</style>
